import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

type Mode = 'sign-in' | 'sign-up' | 'magic-link' | 'verify-email';

const LoginPage = () => {
  const { signInWithGoogle, signInWithMagicLink, signUp, signInWithPassword, user, loading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Redirect to pending invite path or home when user is authenticated
  useEffect(() => {
    if (!loading && user) {
      const pendingPath = localStorage.getItem('pendingInvitePath');
      const postAuthRedirect = localStorage.getItem('postAuthRedirect');
      // postAuthRedirect is a one-shot — always consume it so it can't bounce a
      // later, unrelated login. Only honor it when we're actually on its page
      // (i.e. an in-page sign-in from /feedback), never invite + never stale.
      localStorage.removeItem('postAuthRedirect');
      if (pendingPath) {
        localStorage.removeItem('pendingInvitePath');
        navigate(pendingPath, { replace: true });
      } else if (postAuthRedirect && window.location.pathname === postAuthRedirect) {
        navigate(postAuthRedirect, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [user, loading, navigate]);

  // Listen for auth URL errors (e.g. otp_expired from failed PKCE)
  useEffect(() => {
    const handler = (e: Event) => {
      const { errorCode } = (e as CustomEvent).detail;
      if (errorCode === 'otp_expired') {
        toast({
          title: 'שגיאה',
          description: 'פג תוקף הקישור או שכבר נעשה בו שימוש. אנא נסה להתחבר שוב.',
          variant: 'destructive',
        });
      }
    };
    window.addEventListener('auth-url-error', handler);
    return () => window.removeEventListener('auth-url-error', handler);
  }, [toast]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState<Mode>('sign-in');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [signedUpEmail, setSignedUpEmail] = useState('');

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    const { error } = await signInWithMagicLink(email);
    setSending(false);
    if (error) {
      toast({ title: 'שגיאה', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'נשלח! ✉️', description: 'בדוק את תיבת המייל שלך' });
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setSending(true);
    const { error } = await signInWithPassword(email, password);
    setSending(false);
    if (error) {
      const msg = error.message?.includes('email_not_confirmed') || error.message?.includes('Email not confirmed')
        ? 'החשבון טרם הופעל. אנא בדוק את תיבת המייל שלך (וגם בספאם) ולחץ על קישור האימות.'
        : 'אימייל או סיסמה שגויים';
      toast({ title: 'שגיאה', description: msg, variant: 'destructive' });
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError(null);
    if (!email || !password) return;
    if (password.length < 6) {
      setPasswordError('הסיסמה חייבת להכיל לפחות 6 תווים');
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError('הסיסמאות לא תואמות');
      return;
    }
    setSending(true);
    const { error } = await signUp(email, password);
    setSending(false);
    if (error) {
      const message = error.message.includes('User already registered')
        ? 'המייל כבר רשום, נסה להתחבר'
        : error.message;
      toast({ title: 'שגיאה', description: message, variant: 'destructive' });
    } else {
      // Show verification screen
      setSignedUpEmail(email);
      setPassword('');
      setConfirmPassword('');
      setMode('verify-email');
    }
  };

  const switchMode = (next: Mode) => {
    setPasswordError(null);
    setPassword('');
    setConfirmPassword('');
    setMode(next);
  };

  return (
    <div className="font-leon min-h-screen bg-[#15191E] text-[#FEFEFE] flex items-center justify-center px-4" dir="rtl">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/images/logo.png" alt="Bracket Run" className="h-12 w-auto" />
          </div>
          <p className="text-white/60 text-sm">תחרות ניחושי פלייאוף NBA בין חברים</p>
        </div>

        <div className="bg-[#1A1F25] border border-white/5 rounded-2xl p-6 space-y-4">
          <Button
            variant="accent"
            size="lg"
            className="w-full gap-2 min-h-[48px]"
            onClick={signInWithGoogle}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            התחבר עם Google
          </Button>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-white/50">או</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          {mode === 'sign-in' && (
            <form onSubmit={handleSignIn} className="space-y-3" autoComplete="on">
              <Input
                type="email"
                name="email"
                autoComplete="username"
                placeholder="הכנס אימייל"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="bg-[#15191E] border border-white/10 text-right min-h-[48px] focus-visible:ring-[#F97116]/50"
                dir="ltr"
              />
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  placeholder="סיסמה"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="bg-[#15191E] border border-white/10 text-right min-h-[48px] pr-10 focus-visible:ring-[#F97116]/50"
                  dir="ltr"
                />
              </div>
              <Button variant="secondary" size="lg" className="w-full gap-2 min-h-[48px]" type="submit" disabled={sending}>
                {sending ? 'מתחבר...' : 'התחבר'}
              </Button>
              <div className="flex flex-col items-center gap-1 pt-1">
                <button
                  type="button"
                  onClick={() => switchMode('sign-up')}
                  className="text-xs text-white/50 hover:text-white transition-colors"
                >
                  אין לך חשבון? <span className="text-[#F97116] underline">הרשמה</span>
                </button>
                <button
                  type="button"
                  onClick={() => switchMode('magic-link')}
                  className="text-xs text-white/50 hover:text-white transition-colors"
                >
                  <span className="underline">התחבר עם לינק באימייל</span>
                </button>
              </div>
            </form>
          )}

          {mode === 'sign-up' && (
            <form onSubmit={handleSignUp} className="space-y-3" autoComplete="on">
              <Input
                type="email"
                name="email"
                autoComplete="username"
                placeholder="הכנס אימייל"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="bg-[#15191E] border border-white/10 text-right min-h-[48px] focus-visible:ring-[#F97116]/50"
                dir="ltr"
              />
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="password"
                  name="new-password"
                  autoComplete="new-password"
                  placeholder="סיסמה (לפחות 6 תווים)"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="bg-[#15191E] border border-white/10 text-right min-h-[48px] pr-10 focus-visible:ring-[#F97116]/50"
                  dir="ltr"
                />
              </div>
              <div className="relative">
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="password"
                  name="confirm-password"
                  autoComplete="new-password"
                  placeholder="אימות סיסמה"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="bg-[#15191E] border border-white/10 text-right min-h-[48px] pr-10 focus-visible:ring-[#F97116]/50"
                  dir="ltr"
                />
              </div>
              {passwordError && (
                <p className="text-destructive text-xs text-right">{passwordError}</p>
              )}
              <Button variant="secondary" size="lg" className="w-full gap-2 min-h-[48px]" type="submit" disabled={sending}>
                {sending ? 'נרשם...' : 'הרשמה'}
              </Button>
              <div className="flex justify-center pt-1">
                <button
                  type="button"
                  onClick={() => switchMode('sign-in')}
                  className="text-xs text-white/50 hover:text-white transition-colors"
                >
                  יש לך חשבון? <span className="text-[#F97116] underline">התחבר</span>
                </button>
              </div>
            </form>
          )}

          {mode === 'magic-link' && (
            <form onSubmit={handleMagicLink} className="space-y-3" autoComplete="on">
              <Input
                type="email"
                name="email"
                autoComplete="username"
                placeholder="הכנס אימייל"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="bg-[#15191E] border border-white/10 text-right min-h-[48px] focus-visible:ring-[#F97116]/50"
                dir="ltr"
              />
              <Button variant="secondary" size="lg" className="w-full gap-2 min-h-[48px]" type="submit" disabled={sending}>
                <Mail className="w-4 h-4" />
                {sending ? 'שולח...' : 'שלח לינק'}
              </Button>
              <div className="flex justify-center pt-1">
                <button
                  type="button"
                  onClick={() => switchMode('sign-in')}
                  className="text-xs text-white/50 hover:text-white transition-colors"
                >
                  <span className="underline">התחבר עם סיסמה</span>
                </button>
              </div>
            </form>
          )}

          {mode === 'verify-email' && (
            <div className="space-y-4 text-center py-2">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
              <h2 className="text-lg font-bold text-[#FEFEFE]">נרשמת בהצלחה!</h2>
              <p className="text-sm text-white/70 leading-relaxed" dir="rtl">
                שלחנו קישור אימות ל-<span className="text-foreground font-medium" dir="ltr">{signedUpEmail}</span>
              </p>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <p className="text-sm text-[#F97116] font-semibold mb-1" dir="rtl">
                  חובה ללחוץ על הקישור כדי להפעיל את החשבון
                </p>
                <p className="text-xs text-white/50" dir="rtl">
                  בדוק גם בתיקיית הספאם
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEmail(signedUpEmail);
                  switchMode('sign-in');
                }}
                className="text-xs text-white/50 hover:text-white transition-colors"
              >
                אימתת את המייל? <span className="text-[#F97116] underline">התחבר עכשיו</span>
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default LoginPage;
